/**
 * Cliente HTTP único para comunicación con la API
 * Maneja headers, errores y normalización de respuestas
 */

import type { ApiError, ApiResponse } from '../types/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';
const API_KEY = import.meta.env.VITE_API_KEY || 'formsApiKey';

// Callback para manejar errores de autenticación (401)
let onUnauthorizedCallback: (() => void) | null = null;

/**
 * Registra un callback que se ejecutará cuando se detecte un error 401 (Unauthorized)
 * @param callback Función a ejecutar cuando se detecte un token expirado o inválido
 */
export function setUnauthorizedHandler(callback: () => void) {
  onUnauthorizedCallback = callback;
}

export class HttpClient {
  private baseUrl: string;
  private apiKey: string;

  constructor(baseUrl: string = API_BASE_URL, apiKey: string = API_KEY) {
    this.baseUrl = baseUrl.replace(/\/$/, ''); // Remove trailing slash
    this.apiKey = apiKey;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit & { token?: string } = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const { token, ...requestOptions } = options;

    // Headers base - solo los necesarios
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-API-Key': this.apiKey,
      ...(requestOptions.headers as Record<string, string> || {}),
    };

    // Agregar Authorization header si se proporciona token
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...requestOptions,
        headers,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Detectar error 401 (Unauthorized) - token expirado o inválido
        if (response.status === 401 && onUnauthorizedCallback) {
          // Ejecutar callback para cerrar sesión
          onUnauthorizedCallback();
        }

        const error: ApiError = {
          error: data.error || `HTTP ${response.status}`,
          message: data.message || response.statusText,
          details: {
            ...data.details,
            // Incluir campos adicionales del error (como missing, bySection para 422)
            ...(data.missing && { missing: data.missing }),
            ...(data.bySection && { bySection: data.bySection }),
            statusCode: response.status,
          },
        };

        return { error };
      }

      return { data: data as T };
    } catch (err) {
      const error: ApiError = {
        error: 'NETWORK_ERROR',
        message: err instanceof Error ? err.message : 'Error de conexión',
        details: { originalError: err },
      };

      return { error };
    }
  }

  async get<T>(endpoint: string, token?: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', token });
  }

  async post<T>(endpoint: string, body?: unknown, token?: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      token,
    });
  }

  async put<T>(endpoint: string, body?: unknown, token?: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      token,
    });
  }

  async patch<T>(endpoint: string, body?: unknown, token?: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
      token,
    });
  }

  async delete<T>(endpoint: string, token?: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE', token });
  }

  async postFormData<T>(endpoint: string, formData: FormData, token?: string): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    // Para FormData, no establecemos Content-Type manualmente
    // El navegador lo establecerá automáticamente con el boundary correcto
    // No agregamos headers Cache-Control aquí para evitar problemas de CORS en POST
    const headers: HeadersInit = {
      'X-API-Key': this.apiKey,
    };

    // Agregar Authorization header si se proporciona token
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Detectar error 401 (Unauthorized) - token expirado o inválido
        if (response.status === 401 && onUnauthorizedCallback) {
          // Ejecutar callback para cerrar sesión
          onUnauthorizedCallback();
        }

        const error: ApiError = {
          error: data.error || `HTTP ${response.status}`,
          message: data.message || response.statusText,
          details: {
            ...data.details,
            // Incluir campos adicionales del error (como missing, bySection para 422)
            ...(data.missing && { missing: data.missing }),
            ...(data.bySection && { bySection: data.bySection }),
            statusCode: response.status,
          },
        };

        return { error };
      }

      return { data: data as T };
    } catch (err) {
      const error: ApiError = {
        error: 'NETWORK_ERROR',
        message: err instanceof Error ? err.message : 'Error de conexión',
        details: { originalError: err },
      };

      return { error };
    }
  }
}

export const httpClient = new HttpClient();

